// src/context/AgentContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // Removed useParams as it's less direct here
import { agentService } from '../api/agentService';
import { toast } from 'react-toastify'; // For error feedback

const AgentContext = createContext();

export const AgentProvider = ({ children }) => {
  const [currentAgentId, setCurrentAgentIdInternal] = useState(null);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [loadingAgentContext, setLoadingAgentContext] = useState(false); // Tracks loading of the agent object
  const [isNavigatingToAgent, setIsNavigatingToAgent] = useState(false); // Prevent premature checks during navigation

  const navigate = useNavigate();
  const location = useLocation();

  const loadAgentDetails = useCallback(async (agentIdToLoad) => {
    if (!agentIdToLoad) {
      setCurrentAgent(null);
      setLoadingAgentContext(false); // Ensure loading is false if no ID
      return null;
    }
    setLoadingAgentContext(true);
    setCurrentAgent(null); // Clear previous agent while loading new one
    const res = await agentService.getAgent(agentIdToLoad);
    if (!res.isError) {
      setCurrentAgent(res);
      setLoadingAgentContext(false);
      return res; // Return the fetched agent
    } else {
      console.error("Failed to load current agent details:", res.message);
      toast.error(`Agent ${agentIdToLoad.substring(0,8)}... not found or error: ${res.message}`);
      setCurrentAgent(null);
      setLoadingAgentContext(false);
      // Navigate away if agent not found *after* attempting to load
      // This prevents redirecting if the user is directly typing a URL
      // and we want to show a loader first.
      if (location.pathname.startsWith(`/agent-workspace/${agentIdToLoad}`)) {
         navigate('/agents', { replace: true });
      }
      return null;
    }
  }, [navigate, location.pathname]); // Added location.pathname

  // Effect to sync currentAgentId with URL if navigating directly
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const agentIdFromUrl = (pathSegments[1] === 'agent-workspace' && pathSegments[2]) ? pathSegments[2] : null;

    if (agentIdFromUrl && agentIdFromUrl !== currentAgentId) {
      // If URL has an agent ID and it's different from context, update context and load
      setCurrentAgentIdInternal(agentIdFromUrl);
      loadAgentDetails(agentIdFromUrl);
    } else if (!agentIdFromUrl && currentAgentId) {
      // If URL is not an agent workspace but context still has an agent, clear it
      // This handles navigating away from an agent workspace
      setCurrentAgentIdInternal(null);
      setCurrentAgent(null);
    }
  }, [location.pathname, currentAgentId, loadAgentDetails]);


  const selectAgentAndNavigate = useCallback(async (agentIdToSelect) => {
    if (!agentIdToSelect) {
        deselectAgent();
        return;
    }
    
    setIsNavigatingToAgent(true); // Signal that navigation is in progress
    setCurrentAgentIdInternal(agentIdToSelect); // Set ID first
    
    const fetchedAgent = await loadAgentDetails(agentIdToSelect); // Load details

    if (fetchedAgent) {
        // Navigate only after agent is successfully loaded (or attempted)
        if (location.pathname !== `/agent-workspace/${agentIdToSelect}/overview`) {
             navigate(`/agent-workspace/${agentIdToSelect}/overview`);
        }
    } else {
        // If agent fetch failed, loadAgentDetails handles navigation or error toast
        // We might not want to navigate to overview if it failed.
        // The error state will be handled by AgentWorkspaceLayout or loadAgentDetails.
    }
    setIsNavigatingToAgent(false);
  }, [loadAgentDetails, navigate, location.pathname]);


  const deselectAgent = useCallback(() => {
    setCurrentAgentIdInternal(null);
    setCurrentAgent(null);
    if (location.pathname.startsWith('/agent-workspace')) {
        navigate('/dashboard');
    }
  }, [navigate, location.pathname]);

  return (
    <AgentContext.Provider value={{ 
        currentAgentId, 
        setCurrentAgentId: selectAgentAndNavigate, // Use the new selection function
        currentAgent, 
        loadingAgentContext,
        isNavigatingToAgent, // Expose this if AgentWorkspaceLayout needs it
        deselectAgent
      }}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgentContext = () => useContext(AgentContext);